import { Component, inject, Input } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { ColorPickerModule } from 'primeng/colorpicker';
import { DividerModule } from 'primeng/divider';
import { DropdownModule } from 'primeng/dropdown';
import { FormFieldWrapperComponent } from '../../molecules/form-field-wrapper/form-field-wrapper.component';
import { InputTextModule } from 'primeng/inputtext';
import { NgForOf, NgIf } from '@angular/common';
import { PaginatorModule } from 'primeng/paginator';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DataLayerType } from '../../../models/data-layer-type';
import { AppUtilityService } from '../../../utility/app.utility';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { yearValidator } from '../../../validators/year.validator';
import { patchState } from '@ngrx/signals';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { DataLayer } from '../../../models/data-layer';
import { IconDropdownComponent } from '../../atoms/icon-dropdown/icon-dropdown.component';
import { TagDropdownComponent } from '../../atoms/tag-dropdown/tag-dropdown.component';
import { ActivatedRoute, Router } from '@angular/router';
import { RoutingService } from '../../../services/routing.service';
import { BaseFormComponent } from '../../molecules/base-form/base-form.component';
import { DataLayerTypeStore } from '../../../stores/data-layer-type/data-layer-type.state';
import { WorkspaceDropdownComponent } from '../../atoms/workspace-dropdown/workspace-dropdown.component';
import { PermissionService } from '../../../services/permission.service';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { FormFooterComponent } from '../../molecules/form-footer/form-footer.component';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-poi-data-layer-type-form',
  standalone: true,
  imports: [
    ButtonModule,
    ColorPickerModule,
    DividerModule,
    DropdownModule,
    FormFieldWrapperComponent,
    InputTextModule,
    NgForOf,
    PaginatorModule,
    ReactiveFormsModule,
    InputTextareaModule,
    NgIf,
    IconDropdownComponent,
    TagDropdownComponent,
    WorkspaceDropdownComponent,
    FormFooterComponent,
    TooltipModule,
  ],
  templateUrl: './poi-data-layer-type-form.component.html',
  styleUrl: './poi-data-layer-type-form.component.scss',
})
export class PoiDataLayerTypeFormComponent extends BaseFormComponent<DataLayerType> {
  readonly store = inject(DataLayerTypeStore);
  readonly dataLayerStore = inject(DataLayerStore);
  override form: FormGroup = new FormGroup({});
  override itemToEdit?: DataLayerType;
  @Input() allowInitialYears = true;
  initialYears: DataLayer[] = [];

  typeOptions = [{ label: 'Standort (Point)', value: 'Point' }];

  constructor(
    router: Router,
    route: ActivatedRoute,
    appUtilityService: AppUtilityService,
    routingService: RoutingService,
    permissionService: PermissionService,
    confirmationDialogService: ConfirmationDialogService,
    private fb: FormBuilder
  ) {
    super(
      router,
      route,
      appUtilityService,
      routingService,
      permissionService,
      confirmationDialogService
    );
  }

  /**
   * Initializes the form with predefined fields and values.
   * If an item is selected for editing, its properties are used
   * to pre-fill the form fields. If no item is selected,
   * additional controls are added to the form with default values.
   *
   * @return {void} Does not return any value.
   */
  initializeForm(): void {
    this.itemToEdit = this.store.selectedItem();
    this.form = this.fb.group({
      name: [this.itemToEdit?.name || '', Validators.required],
      geoSource: [this.itemToEdit?.geoSource || ''],
      tags: [this.itemToEdit?.tags || []],
      color: [this.itemToEdit?.color || '#000000', Validators.required],
      icon: [this.itemToEdit?.icon || '', Validators.required],
      type: [this.itemToEdit?.type || 'Point', Validators.required],
      workspaces: [this.itemToEdit?.workspaces || []],
    });

    if (!this.itemToEdit) {
      this.form.addControl('initialYears', this.fb.array([]));
      this.addYear();
    }
    this.formInitialized = true;
  }

  /**
   * Retrieves the FormArray associated with the 'initialYears' control from the form.
   *
   * @return {FormArray} The FormArray instance corresponding to the 'initialYears' control.
   */
  public get initialYearsFormArray(): FormArray {
    return this.form.get('initialYears') as FormArray;
  }

  /**
   * Retrieves an array of plain year values extracted from the `initialYearsFormArray`.
   * Each item is cast to a number.
   *
   * @return {number[]} An array of year numbers.
   */
  public get plainYears(): number[] {
    return this.initialYearsFormArray.value.map(
      (yearControl: { year: number }) => {
        return +yearControl.year;
      }
    );
  }

  /**
   * Adds a new year input form control to the initialYearsFormArray with validation applied.
   *
   * @return {void} This method does not return any value.
   */
  addYear(): void {
    const colorGroup = this.fb.group({
      year: ['', yearValidator(this.plainYears)],
    });

    this.initialYearsFormArray.push(colorGroup);
  }

  /**
   * Removes a year from the initialYearsFormArray at the specified index.
   *
   * @param {number} index The zero-based index of the year to be removed from the array.
   * @return {void} Does not return a value.
   */
  removeYear(index: number): void {
    this.initialYearsFormArray.removeAt(index);
  }

  /**
   * remove data-layer-type confirmation
   * @param item
   */
  removeDataLayerType(item: DataLayerType) {
    this.confirmationDialogService.confirmDataLayerParentDeletion(
      undefined,
      item,
      () => {
        item.message = {
          severity: 'success',
          summary: 'Löschen erfolgreich!',
          detail: `"${item.name}" und die zugehörigen Jahre, sowie Daten wurden gelöscht.`,
          life: 3000,
        };
        this.remove(item);
        this.routingService.navigateBack(2);
      }
    );
  }

  /**
   * Handles the submission process of the form.
   * If the form is valid and an initial years form array exists, it updates the initial years state.
   * After state updates, the submit action is triggered.
   *
   * @return {void} This method does not return any value.
   */
  onSubmit(): void {
    if (this.form.valid) {
      if (this.initialYearsFormArray) {
        this.initialYears = this.initialYearsFormArray.value;
        patchState(this.store, {
          triggerOptions: {
            initialYears: this.initialYears,
            type: this.form.get('type')?.value || 'Point',
          },
        });
      }
    }
    this.submit();
  }
}
